import { message, Spin } from 'antd'
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { takeReturnTo } from '@/auth/returnTo'
import { type AuthorizeUserResponse, authService } from '@/services/authService'

function decodeAuthData(encoded: string): AuthorizeUserResponse {
  // Backend uses URL_SAFE_NO_PAD: swap URL-safe chars and restore `=` padding.
  const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '==='.slice((b64.length + 3) % 4)
  const parsed = JSON.parse(atob(padded)) as Partial<AuthorizeUserResponse>

  if (!parsed.accessToken || !parsed.refreshToken || !parsed.userData) {
    throw new Error('Invalid auth_data payload')
  }
  return parsed as AuthorizeUserResponse
}

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const processedRef = useRef(false)

  useEffect(() => {
    if (processedRef.current) return
    processedRef.current = true

    const authData = searchParams.get('auth_data')
    const errorMessage = searchParams.get('error')

    // Strip params so tokens don't linger in browser history.
    window.history.replaceState({}, '', '/auth/callback')

    void (async () => {
      if (errorMessage) {
        void message.error(errorMessage)
        void navigate('/login')
        return
      }
      try {
        if (!authData) throw new Error('Missing auth_data')
        authService.saveAuthResponse(decodeAuthData(authData))
        void navigate(takeReturnTo() ?? '/')
      } catch (error) {
        console.error('Failed to process auth callback', error)
        void message.error(
          error instanceof Error
            ? error.message
            : 'Authentication failed. Please try again.',
        )
        void navigate('/login')
      }
    })()
  }, [searchParams, navigate])

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Spin size="large" tip="Authenticating..." />
    </div>
  )
}
